import Vue from 'vue'
import axios from 'axios'
import { apolloClient } from '@/apollo'
import gql from 'graphql-tag'
import config from '@/config'

export const fetchUsersQuery = gql`
  query SearchUsers {
    user(order_by: {created_at: asc}) {
      id
      user_name
      first_name
      last_name
      email
      activated_at
      locked_at
      created_at
    }
  }
`
export const fetchOrganizationUsersQuery = gql`
  query SearchOrganizationUsers($organization_id: uuid!) {
    organization_user(where: { organization_id: { _eq: $organization_id} }, order_by: {created_at: asc}) {
      id
      user_id
      user {
        user_name
        first_name
        last_name
        email
        locked_at
        created_at
      }
      organization_id
      organization {
        name
      }
      created_at
    }
  }
`

export const fetchUserQuery = gql`
  query SearchUser($user_id: uuid!) {
    user(where: {id: {_eq: $user_id}}) {
      id
      user_name
      first_name
      last_name
      email
      activated_at
      locked_at
      created_at
      role
      phone
      contact_type
      internal_code
      organization_users {
        organization_id
      }
      blocking_data_modification
      blocking_reason
      blocking_user
      departments_associated_role
      notification_preferences
      type_notifications
      partnership_buyer_companies
    }
  }
`

export const createUserMutation = gql`
  mutation CreateUser($data: [user_insert_input!]!) {
    insert_user(objects: $data) {
      affected_rows
      returning {
        id
      }
    }
  }
`

export const createOrganizationUserMutation = gql`
  mutation CreateOrganizationUser($data: [organization_user_insert_input!]!) {
    insert_organization_user(objects: $data) {
      affected_rows
      returning {
        id
      }
    }
  }
`

export const updateUserMutation = gql`
  mutation UpdateUser($userId: uuid!, $data: user_set_input!) {
    update_user(where: {id: {_eq: $userId}}, _set: $data) {
      affected_rows
      returning {
        id
        user_name
        first_name
        last_name
        email
        activated_at
        locked_at
        created_at
        contact_type
        internal_code
        organization_users {
          organization_id
        }
      }
    }
  }
`

export const removeUserOrganizationMutation = gql`
  mutation RemoveUserOrganization($userId: uuid!) {
    delete_organization_user(where: {user_id: {_eq: $userId}}){
      affected_rows
        returning {
          id
        }
    }
  }
`

export const GET_DROPDOWN_INFORMATION_PROVIDER_TYPE = gql`
  query GET_DROPDOWN_INFORMATION_PROVIDER_TYPE($user_id: String!) {
    notification_type: activation_provider_types(
      where: {
        is_active: { _eq: true }
        combo_objeto: { _eq: "type_notifications" }
      }
      order_by: { name: asc }
    ) {
      id
      code
      name
      __typename
    }
    notification_preferences: activation_provider_types(
      where: {
        is_active: { _eq: true }
        combo_objeto: { _eq: "notification_preferences" }
      }
      order_by: { name: asc }
    ) {
      id
      code
      name
      __typename
    }
    department_associtated_role: activation_provider_types(
      where: {
        is_active: { _eq: true }
        combo_objeto: { _eq: "departments_associated_role" }
      }
      order_by: { name: asc }
    ) {
      id
      code
      name
      __typename
    }
    associated_buying_companies: userBuyers(user_id: $user_id) {
      EnterpriseId
      Name
    }
  }
`

export const orgIsProvider = gql`
  query OrgIsProvider($org_id: uuid!) {
    catalog_aggregate(where: {organization_id: {_eq: $org_id}, catalog_type_id: {_eq: 1}}) {
      aggregate {
        count
      }
    }
  }
`

const user = {
  namespaced: true,
  state: {
    notificationTypeDropdown: [],
    notificationPreferencesDropdown: [],
    departmentAssocitatedRoleDropdown: [],
    associatedBuyingCompanies: [],
    organization_user: {},
    user: {},
    remove: {},
    users: [],
    validatorResult: {}
  },
  actions: {
    async GET_DROPDOWN_INFORMATION_PROVIDER_TYPE ({ state }, variables) {
      try {
        const requestQuery = {
          query: GET_DROPDOWN_INFORMATION_PROVIDER_TYPE,
          variables
        }
        const result = await apolloClient.query(requestQuery)
        if (result && result.errors) throw new Error(result.errors[0].message)
        state.notificationTypeDropdown = result.data.notification_type
        state.notificationPreferencesDropdown = result.data.notification_preferences
        state.departmentAssocitatedRoleDropdown = result.data.department_associtated_role
        state.associatedBuyingCompanies = result.data.associated_buying_companies
      } catch (err) {
        window.console.error(err)
      }
    },
    async orgIsProvider ({ state }, variables) {
      try {
        const requestQuery = {
          query: orgIsProvider,
          variables
        }
        const result = await apolloClient.query(requestQuery)
        if (result && result.errors) throw new Error(result.errors[0].message)
        return result.data.catalog_aggregate.aggregate.count > 0
      } catch (err) {
        window.console.error(err)
      }
    },
    init ({ state }) {
      try {
        Vue.set(state, 'user', {})
        Vue.set(state, 'users', [])
      } catch (error) {
        throw new Error(error)
      }
    },
    async fetchAll ({ commit }, organizationId) {
      try {
        commit('SET_USERS', [])
        const requestQuery = {
          fetchPolicy: 'network-only',
          query: fetchOrganizationUsersQuery,
          variables: {
            organization_id: organizationId
          }
        }

        const result = await apolloClient.query(requestQuery)
        if (result && result.data) {
          commit('SET_USERS', result.data.organization_user)
        }
      } catch (err) {
        window.console.error(err)
      }
    },
    async get ({ commit }, userId) {
      try {
        const requestQuery = {
          fetchPolicy: 'network-only',
          query: fetchUserQuery,
          variables: {
            user_id: userId
          }
        }

        const response = await apolloClient.query(requestQuery)
        if (response && response.data) {
          commit('SET_USER', response.data.user[0])
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    async create ({ commit }, user) {
      try {
        const insertMutation = {
          mutation: createUserMutation,
          variables: {
            data: {
              id: user.id,
              user_name: user.user_name,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              role: user.role,
              // phone: user.phone,
              contact_type: user.contact_type,
              internal_code: user.internal_code,
              activated_at: new Date().toISOString()
            }
          }
        }

        const result = await apolloClient.mutate(insertMutation)
        if (result && result.data) {
          commit('SET_USER', result.data.insert_user.returning[0])
        }

        return result
      } catch (error) {
        window.console.error(error)
        throw new Error(error)
      }
    },
    async createOrganizationUser ({ commit }, organizationUser) {
      try {
        const insertMutation = {
          mutation: createOrganizationUserMutation,
          variables: {
            data: {
              organization_id: organizationUser.organizationId,
              user_id: organizationUser.userId
            }
          }
        }

        const result = await apolloClient.mutate(insertMutation)
        if (result && result.data) {
          commit('SET_USER', result.data.insert_organization_user.returning[0])
        }

        return result
      } catch (error) {
        window.console.error(error)
        throw new Error(error)
      }
    },
    async update ({ commit }, user) {
      try {
        const updateMutation = {
          mutation: updateUserMutation,
          variables: {
            userId: user.id,
            data: {
              id: user.newid,
              user_name: user.user_name,
              first_name: user.first_name,
              last_name: user.last_name,
              role: user.role,
              // phone: user.phone,
              contact_type: user.contact_type,
              internal_code: user.internal_code,
              email: user.email
            }
          }
        }
        const result = await apolloClient.mutate(updateMutation)
        if (result && result.data && result.data.update_user.affected_rows === 1) {
          commit('SET_USER', result.data.update_user.returning[0])
        }

        return result
      } catch (error) {
        throw new Error(error)
      }
    },
    async remove ({ commit }, user) {
      try {
        const removeMutation = {
          mutation: removeUserOrganizationMutation,
          variables: {
            userId: user
          }
        }
        const result = await apolloClient.mutate(removeMutation)
        if (result && result.data && result.data.delete_organization_user.affected_rows === 1) {
          commit('SET_USER_REMOVE', result.data.delete_organization_user.returning[0])
        }

        return result
      } catch (error) {
        throw new Error(error)
      }
    },
    async upload ({ commit }, formData) {
      axios.post(`${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/user/upload`, formData,
        { headers: {'Content-Type': 'multipart/form-data'} }
      ).then(function (response) {
        commit('SET_VALIDATOR_RESULTS', response.data)
      }).catch(function (e) {
        console.error('UPLOAD FAILURE!!')
        Promise.reject(e)
      })
    },
    async massiveUpload ({ commit }, formData) {
      axios.post(`${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/user/massive`, formData,
        { headers: {'Content-Type': 'multipart/form-data'} }
      ).then(function (response) {
        commit('SET_VALIDATOR_RESULTS', response.data)
      }).catch(function (e) {
        console.error('UPLOAD FAILURE!!')
        Promise.reject(e)
      })
    }
  },
  mutations: {
    SET_USERS (state, data) {
      Vue.set(state, 'users', data)
    },
    SET_USER (state, data) {
      Vue.set(state, 'user', data)
    },
    SET_USER_REMOVE (state, data) {
      Vue.set(state, 'remove', data)
    },
    SET_ORGANIZATION_USER (state, data) {
      Vue.set(state, 'organization_user', data)
    },
    SET_VALIDATOR_RESULTS (state, data) {
      Vue.set(state, 'validatorResult', data)
    }
  },
  getters: {
    usersInfo: state => state.users,
    validatorResultInfo: state => state.validatorResult,
    userInfo: state => state.user,
    removeInfo: state => state.remove
  }
}

export default user



// WEBPACK FOOTER //
// ./src/store/modules/user.js